/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import {
  Dialog,
  Typography,
  Grid,
  DialogContent,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'utils/makeStylesAdapter';
import { useDropzone } from 'react-dropzone';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { toast } from 'react-toastify';
import { HeaderModal, ButtonConsensus, InputConsensus } from 'components';
import { colors } from 'utils/colorsSteps';
import { onCloseWithBackdropDialog } from 'utils/materialUI/backdropClickDialog';
import { MenuItem, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import investigatedCertificateService from 'services/investigated-certificates/investigatedCertificateService';
import ClearIcon from '@mui/icons-material/Clear';
import intimationService from 'services/intimation/intimationService';

const useStyles = makeStyles(() => ({
  dropzone: {
    height: '100%',
    width: '100%',
    border: '1px dashed #292929',
    display: 'flex',
    justifyContent: 'center',
    borderRadius: 10,
    cursor: 'pointer',
  },
  inputMaterial: {
    width: '100%',
  },
}));

const ModalAddFile = props => {
  const {
    open,
    onClose,
    inquiry,
    existingCertificatesFiles,
    selectedType,
    victim,
    getCertificates,
    intimations,
  } = props;
  const classes = useStyles();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [files, setFiles] = useState([]);
  const [certificatesFiles, setLocalCertificatesFiles] = useState(existingCertificatesFiles || []);
  const [savedCertificates, setSavedCertificates] = useState(existingCertificatesFiles || []);

  const [loading, setLoading] = useState(false);
  const [loadingConfirmationCertificate, setLoadingConfirmationCertificate] = useState(false);

  const intimation = intimations.find(intimation => intimation.isCurrent === true);

  useEffect(() => {
    setLocalCertificatesFiles(existingCertificatesFiles || []);
    setSavedCertificates(existingCertificatesFiles || []);
  }, [existingCertificatesFiles]);

  useEffect(() => {
    if (intimation?.status === 'SUCCESS' && open) {
      setLoadingConfirmationCertificate(true);
      intimationService
        .getIntimationConfirmationCertificates(intimation.id)
        .then(() => {
          getCertificates().then((groupedFiles = {}) => {
            let updatedFiles = [];
            if (selectedType === 'Vítima' && victim?.id) {
              updatedFiles =
                groupedFiles['Vítima']?.filter(file => file.intimation?.victim?.id === victim.id) || [];
            } else {
              updatedFiles = groupedFiles[selectedType] || [];
            }
            setLocalCertificatesFiles(updatedFiles);
            setSavedCertificates(updatedFiles);
          });
        })
        .finally(() => setLoadingConfirmationCertificate(false));
    }
  }, [open]);

  const { getRootProps, getInputProps } = useDropzone({
    multiple: true,
    onDrop: files => getBase64(files),
  });

  const getBase64 = files => {
    files.forEach(file => {
      var reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result;
        setFiles(files => [
          ...files,
          {
            name: file.name,
            base64,
          },
        ]);
      };
      reader.onerror = function(error) {
        toast.warn(error);
      };
    });
  };

  const handleClose = () => {
    const hasChanges = !(
      (!files.length && !certificatesFiles.length) ||
      loading ||
      (certificatesFiles.length > 0 &&
        files.length === 0 &&
        certificatesFiles.every(certificateFile =>
          savedCertificates.some(
            c => c.name === certificateFile.name && c.type === certificateFile.type,
          ),
        ) &&
        savedCertificates.every(certificateFile =>
          certificatesFiles.some(
            c => c.name === certificateFile.name && c.type === certificateFile.type,
          ),
        ))
    );
    if (hasChanges) {
      toast.error('Você possui alterações que não foram salvas!');
      return;
    }

    setFiles([]);
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedType && !!files.length) {
      toast.warning('É necessário selecionar o tipo de certidão');
      return;
    }
    setLocalCertificatesFiles(certificatesFiles => [
      ...certificatesFiles,
      ...files.map(file => ({ ...file, type: selectedType })),
    ]);
    setFiles([]);
  };

  const saveCertificatesList = () => {
    setLoading(true);
    investigatedCertificateService
      .saveCertificateService({
        justification: null,
        type: selectedType,
        certificates: certificatesFiles,
        inquiryInvestigatedId: inquiry.id,
        victimId: victim?.id || null,
      })
      .then(() => {
        setLocalCertificatesFiles(certificatesFiles);
        setSavedCertificates(certificatesFiles);
        toast.success('Certidões adicionadas com sucesso!');
        setFiles([]);
        getCertificates();
        onClose();
      })
      .catch(() => {
        toast.error('Erro ao adicionar certidões');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className={clsx(classes.root)}>
      <Dialog
        fullScreen={fullScreen}
        onClose={(event, reason) => onCloseWithBackdropDialog(event, reason, handleClose)}
        open={open}
        aria-labelledby="responsive-dialog-title"
        className={classes.modal}
        fullWidth
        maxWidth="md"
      >
        <HeaderModal
          backgroundColor={colors.navBar}
          colorIconTitle={colors.greySecundary}
          onClose={handleClose}
        >
          Adicionar Certidões para tipo {selectedType}
          {selectedType === 'Vítima' && victim?.name && ` (${victim.name})`}
        </HeaderModal>
        <DialogContent>
          <div>
            <Grid container spacing={2}>
              {selectedType !== 'Juízo' && (
                <Grid
                  style={{ minHeight: 150, padding: 20 }}
                  size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <div {...getRootProps({ className: 'dropzone' })} className={classes.dropzone}>
                    <input {...getInputProps()} disabled={loading} />
                    <Typography>Arraste um arquivo, ou clique para adicionar</Typography>
                  </div>
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Typography variant="h5">Total de arquivos: {certificatesFiles.length}</Typography>
              </Grid>
            </Grid>
            {loadingConfirmationCertificate ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={100}>
                <CircularProgress color="primary" />
              </Box>
            ) : (
              certificatesFiles.length > 0 && (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Nome</TableCell>
                      <TableCell align="center">Tipo</TableCell>
                      <TableCell align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {certificatesFiles.map((certificate, index) => (
                      <TableRow key={certificate.name}>
                        <TableCell align="center">{certificate.name}</TableCell>
                        <TableCell align="center">{selectedType}</TableCell>
                        <TableCell align="center">
                          <Tooltip arrow placement="top" title="Remover arquivo">
                            <IconButton
                              color="primary"
                              onClick={() => {
                                setLocalCertificatesFiles(certificatesFiles => [
                                  ...certificatesFiles.filter((_item, i) => i !== index),
                                ]);
                              }}
                              disabled={selectedType === 'Juízo'}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            )}
            {files.map((file, i) => (
              <Box display="flex" alignItems="center" key={i} width="100%">
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Box display="flex" alignItems="center">
                    <PictureAsPdfIcon style={{ marginRight: 5 }} />
                    <Typography variant="body1">{file?.name}</Typography>
                  </Box>
                  <IconButton
                    onClick={() =>
                      setFiles(files => files.filter((_item, fileIndex) => fileIndex !== i))
                    }
                  >
                    <HighlightOffIcon style={{ color: '#e81224' }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
            <Grid container style={{ marginTop: 20, marginBottom: 20 }}>
              <Grid
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                {selectedType !== 'Juízo' && (
                  <ButtonConsensus
                    title={
                      (certificatesFiles.length > 0 && files.length === 0) ||
                      (certificatesFiles.length === 0 && savedCertificates.length > 0)
                        ? 'Salvar Certidões'
                        : 'Adicionar'
                    }
                    type="button"
                    disabled={
                      (!files.length && !certificatesFiles.length && !savedCertificates.length) ||
                      loading ||
                      (certificatesFiles.length > 0 &&
                        files.length === 0 &&
                        certificatesFiles.every(certificate =>
                          savedCertificates.some(
                            c => c.name === certificate.name && c.type === certificate.type,
                          ),
                        ) &&
                        savedCertificates.every(certificate =>
                          certificatesFiles.some(
                            c => c.name === certificate.name && c.type === certificate.type,
                          ),
                        ))
                    }
                    onClick={() => {
                      if (!selectedType && !!files.length) {
                        toast.warning('Tipo de certidão inválido');
                        return;
                      }
                      if (
                        (certificatesFiles.length > 0 && files.length === 0) ||
                        (certificatesFiles.length === 0 && savedCertificates.length > 0)
                      ) {
                        saveCertificatesList();
                      } else {
                        handleSubmit();
                      }
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModalAddFile;
